import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="post-harvest--quality-inspection"
// Manages per-container approve/reject decisions for the quality inspection step.
export default class extends Controller {
  static targets = [
    "containerList",
    "containersDataField",
    "approvedCount",
    "rejectedCount",
    "pendingCount",
    "pendingBadge",
    "approvedBadge",
    "rejectedBadge",
    "yieldPercent",
    "rejectionPercent"
  ]

  connect() {
    this.updateUI()
    this.serialize()
  }

  setDecision({ params: { decision }, currentTarget }) {
    const row = currentTarget.closest("[data-container-row]")

    // Check the correct radio
    row.querySelectorAll(`input[data-field="decision"]`).forEach((r) => {
      r.checked = r.value === decision
    })

    // Update toggle buttons and icon states for all options
    const toggle = row.querySelector("[data-decision-toggle]")
    if (toggle) {
      toggle.querySelectorAll("button").forEach((btn) => {
        const isSelected =
          btn.dataset.postHarvestQualityInspectionDecisionParam === decision

        btn.className = isSelected
          ? `btn btn-sm border-0 gap-1 ${
              decision === "approved"
                ? "btn-success text-success-content"
                : "btn-error text-error-content"
            }`
          : "btn btn-sm border-0 gap-1 btn-ghost"

        btn.querySelectorAll("i").forEach((icon) => this.#updateIcon(icon, isSelected))
      })
    }

    // Update card border
    row.classList.remove("border-success/20", "border-error/20", "border-base-200")
    if (decision === "approved") {
      row.classList.add("border-success/20")
    } else {
      row.classList.add("border-error/20")
    }

    // Toggle rejection fields
    const rejectionFields = row.querySelector("[data-rejection-fields]")
    if (rejectionFields) {
      rejectionFields.classList.toggle("hidden", decision !== "rejected")
    }

    this.updateUI()
    this.serialize()
  }

  update() {
    this.updateUI()
    this.serialize()
  }

  updateUI() {
    const rows = this.containerListTarget.querySelectorAll("[data-container-row]")
    let approved = 0
    let rejected = 0
    let pending = 0
    let approvedWeight = 0
    let rejectedWeight = 0
    let totalWeight = 0

    rows.forEach((row) => {
      const radio = row.querySelector("input[data-field='decision']:checked")
      const decision = radio?.value
      const weight = parseFloat(row.dataset.containerWeight) || 0
      totalWeight += weight

      if (decision === "rejected") {
        rejected++
        rejectedWeight += weight
      } else if (decision === "approved") {
        approved++
        approvedWeight += weight
      } else {
        pending++
      }
    })

    if (this.hasApprovedCountTarget)
      this.approvedCountTarget.textContent = approved
    if (this.hasRejectedCountTarget)
      this.rejectedCountTarget.textContent = rejected
    if (this.hasPendingCountTarget)
      this.pendingCountTarget.textContent = pending

    // Show/hide badges based on counts
    if (this.hasPendingBadgeTarget)
      this.pendingBadgeTarget.classList.toggle("hidden", pending === 0)
    if (this.hasApprovedBadgeTarget)
      this.approvedBadgeTarget.classList.toggle("hidden", approved === 0)
    if (this.hasRejectedBadgeTarget)
      this.rejectedBadgeTarget.classList.toggle("hidden", rejected === 0)

    // Derived percentages
    if (this.hasYieldPercentTarget) {
      this.yieldPercentTarget.textContent =
        totalWeight > 0
          ? `${((approvedWeight / totalWeight) * 100).toFixed(1)}%`
          : "—"
    }
    if (this.hasRejectionPercentTarget) {
      this.rejectionPercentTarget.textContent =
        totalWeight > 0
          ? `${((rejectedWeight / totalWeight) * 100).toFixed(1)}%`
          : "—"
    }
  }

  serialize() {
    const rows = this.containerListTarget.querySelectorAll("[data-container-row]")
    const containers = []

    rows.forEach((row) => {
      const containerId = row.dataset.containerId
      const decisionRadio = row.querySelector(
        "input[data-field='decision']:checked"
      )
      const decision = decisionRadio?.value || ""
      if (!decision) return

      const entry = { container_id: parseInt(containerId), decision }

      if (decision === "rejected") {
        entry.rejection_reason =
          row.querySelector("[data-field='rejection_reason']")?.value || ""
        const signedId = row.querySelector("[data-post-harvest--photo-upload-target='signedId']")?.value || ""
        if (signedId) entry.rejection_photo_signed_id = signedId
      }

      containers.push(entry)
    })

    this.containersDataFieldTarget.value = JSON.stringify(containers)
  }

  // --- Private helpers ---

  #updateIcon(icon, isSelected) {
    // Keep icon and button selection state synchronized
    icon.className = isSelected
      ? icon.className.replace(/i-ph-(check|x)-circle\b/, "i-ph-$1-circle-fill")
      : icon.className.replace(/i-ph-(check|x)-circle-fill\b/, "i-ph-$1-circle")
  }
}
